if (!customElements.get('vl-pdp-color-showcase')) {
  class VLPDPColorShowcase extends HTMLElement {
    static CROSSFADE_DURATION = 220;

    static DESKTOP_BREAKPOINT = '(min-width: 768px)';

    static DESKTOP_INITIAL_TOP = 48;

    static MOBILE_INITIAL_GAP = 48;

    static MOBILE_END_GAP = 16;

    static MOTION_DISTANCE_MULTIPLIER = 1;

    static AUTOPLAY_PRELOAD_LEAD_TIME = 1000;

    constructor() {
      super();

      this.isInitialized = false;
      this.abortController = null;
      this.selectionToken = 0;
      this.currentIndex = null;
      this.requestedIndex = null;
      this.pendingIndex = null;
      this.layerCache = new Map();
      this.transitionState = null;
      this.transitionFrame = null;
      this.transitionTimer = null;
      this.motionFrame = null;
      this.motionObserver = null;
      this.previewObserver = null;
      this.autoplayObserver = null;
      this.autoplayTimer = null;
      this.autoplayPreloadTimer = null;
      this.autoplayProgressFrame = null;
      this.autoplayStartedAt = 0;
      this.autoplayElapsed = 0;
      this.autoplayProgressValue = 0;
      this.autoplayProgressSwatch = null;
      this.autoplayInView = false;
      this.autoplayPausedByPointer = false;
      this.autoplayPausedByFocus = false;
      this.motionInView = false;
      this.lastRevealValue = null;
      this.progressiveSources = new WeakMap();
      this.highResolutionLoads = new WeakSet();
      this.previewPreloads = new Map();

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleMotionScroll = this.handleMotionScroll.bind(this);
      this.handleMotionResize = this.handleMotionResize.bind(this);
      this.handleMotionIntersection = this.handleMotionIntersection.bind(this);
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
      this.handleAutoplayIntersection = this.handleAutoplayIntersection.bind(this);
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.updateAutoplayProgress = this.updateAutoplayProgress.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.stage = this.querySelector('[data-vl-color-stage]');
      this.currentLayer = this.querySelector('[data-vl-color-current]');
      this.status = this.querySelector('[data-vl-color-status]');
      this.card = this.querySelector('.vl-pdp-color-showcase__card');
      this.header = this.querySelector('.vl-pdp-color-showcase__header');
      this.subheading = this.querySelector('.vl-pdp-color-showcase__subheading');
      this.buttons = [...this.querySelectorAll('[data-vl-color-button]')];
      this.templates = [...this.querySelectorAll('template[data-vl-color-template]')];

      if (!this.stage || !this.currentLayer) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.buttonMap = new Map();
      this.templateMap = new Map();

      this.buttons.forEach((button, position) => {
        const index = this.getElementIndex(button, position);
        button.dataset.index = index;
        this.buttonMap.set(index, button);
      });

      this.templates.forEach((template, position) => {
        const index = this.getElementIndex(template, position);
        template.dataset.index = index;
        this.templateMap.set(index, template);
      });

      const selectedButton =
        this.buttons.find((button) => button.getAttribute('aria-checked') === 'true') ||
        this.buttons.find((button) => button.classList.contains('is-active')) ||
        this.buttons[0];
      const initialIndex =
        this.currentLayer.dataset.index ||
        selectedButton?.dataset.index ||
        this.buttons[0]?.dataset.index ||
        '0';

      this.currentIndex = String(initialIndex);
      this.requestedIndex = this.currentIndex;
      this.currentLayer.dataset.index = this.currentIndex;
      this.currentLayer.dataset.vlColorLayer = '';
      this.currentLayer.classList.add('is-active');
      this.currentLayer.classList.remove('is-entering', 'is-leaving', 'is-preparing');
      this.currentLayer.setAttribute('data-vl-color-current', '');
      this.currentLayer.setAttribute('aria-hidden', 'false');
      this.currentLayer.inert = false;
      this.layerCache.set(this.currentIndex, {
        layer: this.currentLayer,
        promise: Promise.resolve(this.currentLayer),
        status: 'ready',
      });

      this.updateControls(this.currentIndex);
      this.setPendingState(null);

      this.addEventListener('click', this.handleClick, { signal });
      this.addEventListener('keydown', this.handleKeydown, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      this.initPreviewPreloading();
      this.initMotion(signal);
      this.initAutoplay(signal);
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.selectionToken += 1;
      this.setPendingState(null);
      this.abortController?.abort();
      this.motionObserver?.disconnect();
      this.previewObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      this.clearAutoplayTimer(true);
      this.clearTransition(false);

      if (this.motionFrame !== null) cancelAnimationFrame(this.motionFrame);

      this.layerCache.forEach((entry) => {
        if (entry.layer !== this.currentLayer) entry.layer.remove();
      });

      this.classList.remove('is-motion-active');
      this.abortController = null;
      this.motionObserver = null;
      this.previewObserver = null;
      this.autoplayObserver = null;
      this.motionFrame = null;
      this.autoplayPreloadTimer = null;
      this.autoplayProgressFrame = null;
      this.autoplayStartedAt = 0;
      this.autoplayElapsed = 0;
      this.autoplayProgressValue = 0;
      this.autoplayProgressSwatch = null;
      this.autoplayInView = false;
      this.autoplayPausedByPointer = false;
      this.autoplayPausedByFocus = false;
      this.motionInView = false;
      this.layerCache.clear();
      this.highResolutionLoads = new WeakSet();
      this.previewPreloads.clear();
      this.isInitialized = false;
    }

    getElementIndex(element, fallbackIndex) {
      const value = element.dataset.index;
      return value === undefined || value === '' ? String(fallbackIndex) : String(value);
    }

    handleClick(event) {
      const button = event.target.closest?.('[data-vl-color-button]');
      if (!button || !this.contains(button) || this.isButtonDisabled(button)) return;

      this.selectIndex(button.dataset.index);
      if (event.detail > 0) this.autoplayPausedByFocus = false;
      this.restartAutoplay();
    }

    handleKeydown(event) {
      const button = event.target.closest?.('[data-vl-color-button]');
      if (!button || !this.contains(button)) return;

      const enabledButtons = this.buttons.filter((item) => !this.isButtonDisabled(item));
      if (enabledButtons.length === 0) return;

      const currentPosition = enabledButtons.indexOf(button);
      if (currentPosition === -1) return;

      let targetButton = null;

      if (event.key === 'ArrowLeft') {
        targetButton = enabledButtons[(currentPosition - 1 + enabledButtons.length) % enabledButtons.length];
      } else if (event.key === 'ArrowRight') {
        targetButton = enabledButtons[(currentPosition + 1) % enabledButtons.length];
      } else if (event.key === 'Home') {
        targetButton = enabledButtons[0];
      } else if (event.key === 'End') {
        targetButton = enabledButtons[enabledButtons.length - 1];
      }

      if (!targetButton) return;

      event.preventDefault();
      targetButton.focus({ preventScroll: true });
      this.selectIndex(targetButton.dataset.index);
      this.restartAutoplay();
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const button = this.buttons.find((item) => item.dataset.blockId === String(blockId));
      if (!button) return;

      this.selectIndex(button.dataset.index, { animate: false, announce: false });
      this.restartAutoplay();
    }

    initAutoplay(signal) {
      if (!this.isColorAutoplayEnabled || this.buttons.length < 2) return;

      this.setAutoplayProgress(0);

      this.stage.addEventListener('mouseenter', this.handleMouseEnter, { signal });
      this.stage.addEventListener('mouseleave', this.handleMouseLeave, { signal });
      this.addEventListener('focusin', this.handleFocusIn, { signal });
      this.addEventListener('focusout', this.handleFocusOut, { signal });
      document.addEventListener('visibilitychange', this.handleVisibilityChange, { signal });

      if ('IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver(this.handleAutoplayIntersection, {
          threshold: 0.2,
        });
        this.autoplayObserver.observe(this);
      } else {
        this.autoplayInView = true;
        this.scheduleAutoplay();
      }
    }

    handleAutoplayIntersection(entries) {
      const entry = entries[entries.length - 1];
      if (!entry) return;

      this.autoplayInView = entry.isIntersecting && entry.intersectionRatio >= 0.2;

      if (this.autoplayInView) {
        this.scheduleAutoplay();
      } else {
        this.clearAutoplayTimer();
      }
    }

    handleMouseEnter() {
      this.autoplayPausedByPointer = true;
      this.clearAutoplayTimer();
    }

    handleMouseLeave() {
      this.autoplayPausedByPointer = false;
      this.scheduleAutoplay();
    }

    handleFocusIn() {
      this.autoplayPausedByFocus = true;
      this.clearAutoplayTimer();
    }

    handleFocusOut(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;

      this.autoplayPausedByFocus = false;
      this.scheduleAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.clearAutoplayTimer();
      } else {
        this.scheduleAutoplay();
      }
    }

    restartAutoplay() {
      if (!this.isColorAutoplayEnabled) return;

      this.clearAutoplayTimer(true);
      this.scheduleAutoplay();
    }

    scheduleAutoplay() {
      if (
        !this.canAutoplay ||
        this.autoplayTimer !== null ||
        this.autoplayProgressFrame !== null
      ) {
        return;
      }

      const duration = this.colorAutoplayInterval;
      if (this.autoplayElapsed >= duration) {
        this.setAutoplayProgress(1);
        this.autoplayProgressFrame = requestAnimationFrame(() => {
          this.autoplayProgressFrame = null;
          this.advanceAutoplay();
        });
        return;
      }

      const remaining = Math.max(duration - this.autoplayElapsed, 0);
      this.autoplayStartedAt = performance.now();
      this.classList.add('is-autoplay-progressing');
      this.scheduleAutoplayProgressFrame();
      this.scheduleNextAutoplayPreload(remaining);

      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        this.autoplayElapsed = duration;
        this.autoplayStartedAt = 0;
        this.cancelAutoplayProgressFrame();
        this.classList.remove('is-autoplay-progressing');
        this.setAutoplayProgress(1);
        this.autoplayProgressFrame = requestAnimationFrame(() => {
          this.autoplayProgressFrame = null;
          this.advanceAutoplay();
        });
      }, remaining);
    }

    advanceAutoplay() {
      if (!this.canAutoplay) return;

      const nextButton = this.getNextAutoplayButton();
      if (!nextButton) return;

      this.resetAutoplayProgress();

      Promise.resolve(
        this.selectIndex(nextButton.dataset.index, { announce: false })
      ).finally(() => this.scheduleAutoplay());
    }

    getNextAutoplayButton() {
      const enabledButtons = this.buttons.filter((button) => !this.isButtonDisabled(button));
      if (enabledButtons.length < 2) return null;

      const currentPosition = Math.max(
        enabledButtons.findIndex((button) => button.dataset.index === this.requestedIndex),
        0
      );

      return enabledButtons[(currentPosition + 1) % enabledButtons.length];
    }

    scheduleNextAutoplayPreload(remaining) {
      if (this.autoplayPreloadTimer !== null) {
        window.clearTimeout(this.autoplayPreloadTimer);
        this.autoplayPreloadTimer = null;
      }

      const delay = Math.max(
        remaining - VLPDPColorShowcase.AUTOPLAY_PRELOAD_LEAD_TIME,
        0
      );

      if (delay === 0) {
        this.preloadNextAutoplayLayer();
        return;
      }

      this.autoplayPreloadTimer = window.setTimeout(() => {
        this.autoplayPreloadTimer = null;
        this.preloadNextAutoplayLayer();
      }, delay);
    }

    preloadNextAutoplayLayer() {
      if (!this.canAutoplay || this.abortController?.signal.aborted) return;

      const nextButton = this.getNextAutoplayButton();
      if (!nextButton) return;

      const nextIndex = String(nextButton.dataset.index);
      const cachedEntry = this.layerCache.get(nextIndex);

      if (cachedEntry?.status === 'loading') return;

      if (cachedEntry?.status === 'ready') {
        if (
          !cachedEntry.layer.classList.contains('is-high-resolution') &&
          this.progressiveSources.has(cachedEntry.layer)
        ) {
          this.loadHighResolutionImages(cachedEntry.layer, this.abortController.signal);
        }
        return;
      }

      this.getPreparedLayer(nextIndex).catch((error) => {
        if (error?.name === 'AbortError') return;
      });
    }

    clearAutoplayTimer(resetProgress = false) {
      if (this.autoplayStartedAt > 0) {
        this.autoplayElapsed = Math.min(
          this.autoplayElapsed + (performance.now() - this.autoplayStartedAt),
          this.colorAutoplayInterval
        );
      }

      if (this.autoplayTimer !== null) window.clearTimeout(this.autoplayTimer);
      if (this.autoplayPreloadTimer !== null) window.clearTimeout(this.autoplayPreloadTimer);
      this.autoplayTimer = null;
      this.autoplayPreloadTimer = null;
      this.autoplayStartedAt = 0;
      this.cancelAutoplayProgressFrame();
      this.classList.remove('is-autoplay-progressing');

      if (resetProgress) {
        this.resetAutoplayProgress();
      } else if (this.isColorAutoplayEnabled) {
        this.setAutoplayProgress(this.autoplayElapsed / this.colorAutoplayInterval);
      }
    }

    scheduleAutoplayProgressFrame() {
      if (this.autoplayProgressFrame !== null) return;
      this.autoplayProgressFrame = requestAnimationFrame(this.updateAutoplayProgress);
    }

    updateAutoplayProgress(now) {
      this.autoplayProgressFrame = null;
      if (!this.canAutoplay || this.autoplayTimer === null || this.autoplayStartedAt <= 0) return;

      const elapsed = Math.min(
        this.autoplayElapsed + (now - this.autoplayStartedAt),
        this.colorAutoplayInterval
      );
      this.setAutoplayProgress(elapsed / this.colorAutoplayInterval);
      this.scheduleAutoplayProgressFrame();
    }

    cancelAutoplayProgressFrame() {
      if (this.autoplayProgressFrame === null) return;

      cancelAnimationFrame(this.autoplayProgressFrame);
      this.autoplayProgressFrame = null;
    }

    resetAutoplayProgress() {
      this.autoplayElapsed = 0;
      this.autoplayStartedAt = 0;
      this.setAutoplayProgress(0);
    }

    setAutoplayProgress(progress) {
      const normalizedProgress = this.clamp(Number(progress) || 0, 0, 1);
      this.autoplayProgressValue = normalizedProgress;
      this.autoplayProgressSwatch?.style.setProperty(
        '--vl-pdp-color-showcase-autoplay-progress',
        normalizedProgress.toFixed(4)
      );
    }

    isButtonDisabled(button) {
      return button.disabled || button.getAttribute('aria-disabled') === 'true';
    }

    async selectIndex(index, options = {}) {
      const targetIndex = String(index);
      const button = this.buttonMap.get(targetIndex);
      const template = this.templateMap.get(targetIndex);
      const animate = options.animate !== false;
      const announce = options.announce !== false;

      if (!button || (!template && targetIndex !== this.currentIndex)) return;

      if (targetIndex === this.requestedIndex) {
        this.updateControls(targetIndex);
        return;
      }

      if (this.transitionState) this.finishTransition(false);

      const token = ++this.selectionToken;
      this.requestedIndex = targetIndex;
      this.updateControls(targetIndex);

      if (targetIndex === this.currentIndex) {
        this.setPendingState(null);
        return;
      }

      this.setPendingState(targetIndex);

      let nextLayer;

      try {
        nextLayer = await this.getPreparedLayer(targetIndex);
      } catch (error) {
        if (error?.name === 'AbortError') return;

        if (token === this.selectionToken) {
          this.setPendingState(null);
          this.requestedIndex = this.currentIndex;
          this.updateControls(this.currentIndex);
          this.announceLoadFailure(targetIndex);
        }
        return;
      }

      if (token !== this.selectionToken || !this.isConnected) {
        if (nextLayer !== this.currentLayer) nextLayer.remove();
        return;
      }

      this.setPendingState(null);
      this.showLayer(nextLayer, targetIndex, {
        animate: animate && !this.prefersReducedMotion,
        announce,
      });
    }

    updateControls(index) {
      this.buttons.forEach((button) => {
        const isSelected = button.dataset.index === String(index);
        button.classList.toggle('is-active', isSelected);
        button.setAttribute('aria-checked', String(isSelected));
        button.setAttribute('tabindex', isSelected ? '0' : '-1');
      });

      const activeSwatch = this.buttonMap
        .get(String(index))
        ?.querySelector('.vl-pdp-color-showcase__swatch');

      if (this.autoplayProgressSwatch !== activeSwatch) {
        this.autoplayProgressSwatch?.style.removeProperty(
          '--vl-pdp-color-showcase-autoplay-progress'
        );
        this.autoplayProgressSwatch = activeSwatch || null;
      }

      this.autoplayProgressSwatch?.style.setProperty(
        '--vl-pdp-color-showcase-autoplay-progress',
        this.autoplayProgressValue.toFixed(4)
      );
    }

    setPendingState(index) {
      const pendingIndex = index === null || index === undefined ? null : String(index);
      const isPending = pendingIndex !== null;

      this.pendingIndex = pendingIndex;
      this.classList.toggle('is-pending', isPending);

      if (isPending) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }

      this.buttons?.forEach((button) => {
        const isPendingButton = isPending && button.dataset.index === pendingIndex;
        button.classList.toggle('is-pending', isPendingButton);

        if (isPendingButton) {
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-busy');
        }
      });
    }

    async getPreparedLayer(index) {
      const cachedEntry = this.layerCache.get(index);

      if (cachedEntry?.status === 'ready') return cachedEntry.layer;
      if (cachedEntry?.status === 'loading') return cachedEntry.promise;

      const template = this.templateMap.get(index);
      if (!template) throw new Error(`Missing color media template for index ${index}`);

      const layer = this.cloneTemplateLayer(template, index);
      const entry = {
        layer,
        promise: null,
        status: 'loading',
      };

      layer.classList.add('is-preparing');
      layer.setAttribute('aria-hidden', 'true');
      layer.inert = true;
      layer.style.opacity = '0';
      layer.style.pointerEvents = 'none';
      this.stage.append(layer);

      entry.promise = this.waitForLayerImages(layer, this.abortController.signal)
        .then(() => {
          entry.status = 'ready';
          layer.classList.remove('is-preparing');
          layer.style.removeProperty('opacity');
          layer.style.removeProperty('pointer-events');
          this.loadHighResolutionImages(layer, this.abortController.signal);
          return layer;
        })
        .catch((error) => {
          layer.remove();
          this.layerCache.delete(index);
          throw error;
        });

      this.layerCache.set(index, entry);
      return entry.promise;
    }

    cloneTemplateLayer(template, index) {
      const fragment = template.content.cloneNode(true);
      let layer;

      if (fragment.childElementCount === 1) {
        layer = fragment.firstElementChild;
      } else {
        layer = document.createElement('div');
        layer.append(fragment);
      }

      layer.removeAttribute('data-vl-color-current');
      layer.dataset.vlColorLayer = '';
      layer.dataset.index = index;
      layer.classList.remove('is-active', 'is-entering', 'is-leaving');

      this.prepareLowResolutionImages(layer, template);

      layer.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
        image.decoding = 'async';
      });

      return layer;
    }

    prepareLowResolutionImages(layer, template) {
      const previewDesktop = template.dataset.previewDesktop;
      const previewMobile = template.dataset.previewMobile || previewDesktop;
      const image = layer.querySelector('img');

      if (!image || !previewDesktop) return;

      const source = layer.querySelector('picture source[media]');
      const sources = {
        image,
        source,
        imageSrc: image.getAttribute('src'),
        imageSrcset: image.getAttribute('srcset'),
        imageSizes: image.getAttribute('sizes'),
        sourceSrcset: source?.getAttribute('srcset') || null,
        previewDesktop,
        previewMobile,
      };

      this.progressiveSources.set(layer, sources);

      if (source) source.setAttribute('srcset', previewMobile);

      image.removeAttribute('sizes');
      image.setAttribute('srcset', previewDesktop);
      image.setAttribute('src', previewDesktop);
    }

    loadHighResolutionImages(layer, signal) {
      const sources = this.progressiveSources.get(layer);
      if (!sources || signal.aborted || this.highResolutionLoads.has(layer)) return;

      const { image, source } = sources;
      this.highResolutionLoads.add(layer);

      if (source) {
        if (sources.sourceSrcset) {
          source.setAttribute('srcset', sources.sourceSrcset);
        } else {
          source.removeAttribute('srcset');
        }
      }

      if (sources.imageSizes) {
        image.setAttribute('sizes', sources.imageSizes);
      } else {
        image.removeAttribute('sizes');
      }

      if (sources.imageSrcset) {
        image.setAttribute('srcset', sources.imageSrcset);
      } else {
        image.removeAttribute('srcset');
      }

      if (sources.imageSrc) image.setAttribute('src', sources.imageSrc);

      return this.waitForImage(image, signal)
        .then(() => {
          layer.classList.add('is-high-resolution');
          this.progressiveSources.delete(layer);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;

          image.removeAttribute('sizes');
          image.setAttribute('srcset', sources.previewDesktop);
          image.setAttribute('src', sources.previewDesktop);
          if (source) source.setAttribute('srcset', sources.previewMobile);
        })
        .finally(() => this.highResolutionLoads.delete(layer));
    }

    initPreviewPreloading() {
      if (this.templates.length === 0) return;

      if (!('IntersectionObserver' in window)) {
        this.preloadPreviewImages();
        return;
      }

      this.previewObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;

          this.previewObserver?.disconnect();
          this.previewObserver = null;
          this.preloadPreviewImages();
        },
        { rootMargin: '800px 0px', threshold: 0 }
      );
      this.previewObserver.observe(this);
    }

    preloadPreviewImages() {
      const useMobileImage = window.matchMedia('(max-width: 767px)').matches;

      this.templates.forEach((template) => {
        if (template.dataset.index === this.currentIndex) return;

        const url = useMobileImage
          ? template.dataset.previewMobile || template.dataset.previewDesktop
          : template.dataset.previewDesktop;

        if (!url || this.previewPreloads.has(url)) return;

        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.src = url;
        this.previewPreloads.set(url, image);
      });
    }

    async waitForLayerImages(layer, signal) {
      const images = [...layer.querySelectorAll('img')];
      if (images.length === 0) return;

      await Promise.all(images.map((image) => this.waitForImage(image, signal)));
    }

    waitForImage(image, signal) {
      if (signal.aborted) {
        return Promise.reject(new DOMException('Image loading aborted', 'AbortError'));
      }

      if (image.complete) {
        if (image.naturalWidth === 0) return Promise.reject(new Error('Image failed to load'));
        return this.decodeImage(image);
      }

      return new Promise((resolve, reject) => {
        const cleanup = () => {
          image.removeEventListener('load', handleLoad);
          image.removeEventListener('error', handleError);
          signal.removeEventListener('abort', handleAbort);
        };

        const handleLoad = () => {
          cleanup();
          this.decodeImage(image).then(resolve).catch(reject);
        };

        const handleError = () => {
          cleanup();
          reject(new Error('Image failed to load'));
        };

        const handleAbort = () => {
          cleanup();
          reject(new DOMException('Image loading aborted', 'AbortError'));
        };

        image.addEventListener('load', handleLoad, { once: true });
        image.addEventListener('error', handleError, { once: true });
        signal.addEventListener('abort', handleAbort, { once: true });
      });
    }

    async decodeImage(image) {
      if (typeof image.decode !== 'function') return;

      try {
        await image.decode();
      } catch (error) {
        if (image.naturalWidth === 0) throw error;
      }
    }

    showLayer(nextLayer, index, options) {
      const previousLayer = this.currentLayer;

      if (nextLayer === previousLayer) {
        this.currentIndex = index;
        this.requestedIndex = index;
        if (options.announce) this.announceSelection(index);
        return;
      }

      if (!nextLayer.isConnected) this.stage.append(nextLayer);

      nextLayer.classList.remove('is-active', 'is-leaving', 'is-preparing');
      nextLayer.classList.add('is-entering');
      nextLayer.setAttribute('aria-hidden', 'false');
      nextLayer.inert = false;

      if (!options.animate) {
        this.commitLayer(previousLayer, nextLayer, index, options.announce);
        return;
      }

      this.transitionState = {
        previousLayer,
        nextLayer,
        index,
        announce: options.announce,
      };

      this.transitionFrame = requestAnimationFrame(() => {
        this.transitionFrame = requestAnimationFrame(() => {
          this.transitionFrame = null;
          if (!this.transitionState || this.transitionState.nextLayer !== nextLayer) return;

          previousLayer.classList.remove('is-active');
          previousLayer.classList.add('is-leaving');
          nextLayer.classList.remove('is-entering');
          nextLayer.classList.add('is-active');

          this.transitionTimer = window.setTimeout(() => {
            this.transitionTimer = null;
            this.finishTransition(true);
          }, VLPDPColorShowcase.CROSSFADE_DURATION + 50);
        });
      });
    }

    finishTransition(announce = true) {
      const state = this.transitionState;
      if (!state) return;

      this.clearTransition(false);
      this.commitLayer(
        state.previousLayer,
        state.nextLayer,
        state.index,
        announce && state.announce
      );
    }

    clearTransition(resetLayers = true) {
      if (this.transitionFrame !== null) cancelAnimationFrame(this.transitionFrame);
      if (this.transitionTimer !== null) clearTimeout(this.transitionTimer);

      if (resetLayers && this.transitionState) {
        this.transitionState.previousLayer.classList.remove('is-leaving');
        this.transitionState.previousLayer.classList.add('is-active');
        this.transitionState.nextLayer.classList.remove('is-active', 'is-entering', 'is-leaving');
        this.transitionState.nextLayer.remove();
      }

      this.transitionFrame = null;
      this.transitionTimer = null;
      this.transitionState = null;
    }

    commitLayer(previousLayer, nextLayer, index, announce) {
      previousLayer.classList.remove('is-active', 'is-entering', 'is-leaving');
      previousLayer.removeAttribute('data-vl-color-current');
      previousLayer.setAttribute('aria-hidden', 'true');
      previousLayer.inert = true;
      previousLayer.remove();

      nextLayer.classList.remove('is-entering', 'is-leaving', 'is-preparing');
      nextLayer.classList.add('is-active');
      nextLayer.setAttribute('data-vl-color-current', '');
      nextLayer.setAttribute('aria-hidden', 'false');
      nextLayer.inert = false;

      this.currentLayer = nextLayer;
      this.currentIndex = index;
      this.requestedIndex = index;
      this.updateControls(index);

      if (announce) this.announceSelection(index);
      this.scheduleAutoplay();
    }

    announceSelection(index) {
      if (!this.status) return;

      const template = this.templateMap.get(index);
      const button = this.buttonMap.get(index);
      const colorName =
        template?.dataset.colorName || button?.dataset.colorName || button?.textContent.trim();

      if (colorName) this.status.textContent = `Showing ${colorName}`;
    }

    announceLoadFailure(index) {
      if (!this.status) return;

      const template = this.templateMap.get(index);
      const button = this.buttonMap.get(index);
      const colorName =
        template?.dataset.colorName || button?.dataset.colorName || button?.textContent.trim();

      this.status.textContent = colorName
        ? `${colorName} image could not be loaded`
        : 'The selected image could not be loaded';
    }

    initMotion(signal) {
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotionQuery.addEventListener('change', this.handleMotionPreferenceChange, {
        signal,
      });

      if (!this.isMotionEnabled) {
        this.setRevealValue(0);
        return;
      }

      this.desktopQuery = window.matchMedia(VLPDPColorShowcase.DESKTOP_BREAKPOINT);
      this.desktopQuery.addEventListener('change', this.handleMotionResize, { signal });
      window.addEventListener('scroll', this.handleMotionScroll, { passive: true, signal });
      window.addEventListener('resize', this.handleMotionResize, { passive: true, signal });

      if ('IntersectionObserver' in window) {
        this.motionObserver = new IntersectionObserver(this.handleMotionIntersection, {
          rootMargin: '320px 0px',
          threshold: 0,
        });
        this.motionObserver.observe(this);
      } else {
        this.motionInView = true;
      }

      this.scheduleMotionUpdate(true);
    }

    handleMotionIntersection(entries) {
      const entry = entries[entries.length - 1];
      if (!entry) return;

      this.motionInView = entry.isIntersecting;
      this.scheduleMotionUpdate(true);
    }

    handleMotionScroll() {
      if (!this.motionInView) return;
      this.scheduleMotionUpdate();
    }

    handleMotionResize() {
      this.scheduleMotionUpdate(true);
    }

    handleMotionPreferenceChange() {
      if (this.prefersReducedMotion && this.transitionState) this.finishTransition(false);
      this.scheduleMotionUpdate(true);

      if (this.prefersReducedMotion) {
        this.clearAutoplayTimer(true);
      } else {
        this.scheduleAutoplay();
      }
    }

    scheduleMotionUpdate(force = false) {
      if (this.motionFrame !== null) return;
      if (!force && (!this.isMotionEnabled || this.prefersReducedMotion)) return;

      this.motionFrame = requestAnimationFrame(() => {
        this.motionFrame = null;
        this.updateMotion();
      });
    }

    updateMotion() {
      if (!this.isMotionEnabled || this.prefersReducedMotion) {
        this.setRevealValue(0);
        this.classList.remove('is-motion-active');
        return;
      }

      const isDesktop = this.desktopQuery.matches;
      const rootRect = this.getBoundingClientRect();
      const sectionHalfHeight = rootRect.height / 2;
      const revealDistance = Math.max(
        sectionHalfHeight * VLPDPColorShowcase.MOTION_DISTANCE_MULTIPLIER,
        1
      );
      const sectionCenter = rootRect.top + sectionHalfHeight;
      const revealProgress = this.clamp(
        (window.innerHeight - sectionCenter) / revealDistance,
        0,
        1
      );
      const geometry = this.getMotionGeometry(isDesktop);
      const motionAmplitude = this.getMotionAmplitude(isDesktop);
      const effectiveEndOffset =
        geometry.startOffset +
        (geometry.endOffset - geometry.startOffset) * (motionAmplitude / 100);
      const motionOffset =
        geometry.startOffset +
        (effectiveEndOffset - geometry.startOffset) * revealProgress;
      const remainingClip = isDesktop ? Math.max(motionOffset - geometry.endOffset, 0) : 0;

      this.setRevealValue(motionOffset, remainingClip);
      this.classList.toggle(
        'is-motion-active',
        this.motionInView && revealProgress > 0 && revealProgress < 1
      );
    }

    getMotionGeometry(isDesktop) {
      if (!this.card || !this.stage) {
        return isDesktop
          ? { startOffset: 114, endOffset: 0 }
          : { startOffset: 34, endOffset: 2 };
      }

      const cardRect = this.card.getBoundingClientRect();
      const stageHeight = this.stage.getBoundingClientRect().height;
      const stageTop = this.stage.offsetTop;

      if (isDesktop) {
        const initialTop = this.parseDistance(
          this.dataset.motionInitialTopDesktop,
          VLPDPColorShowcase.DESKTOP_INITIAL_TOP
        );

        return {
          startOffset: initialTop - stageTop,
          endOffset: cardRect.height - (stageTop + stageHeight),
        };
      }

      const referenceElement = this.subheading || this.header;
      if (!referenceElement) return { startOffset: 34, endOffset: 2 };

      const referenceBottomFromCard = referenceElement.getBoundingClientRect().bottom - cardRect.top;
      const initialGap = this.parseDistance(
        this.dataset.motionInitialGapMobile,
        VLPDPColorShowcase.MOBILE_INITIAL_GAP
      );
      const endGap = this.parseDistance(
        this.dataset.motionEndGapMobile,
        VLPDPColorShowcase.MOBILE_END_GAP
      );

      return {
        startOffset: referenceBottomFromCard + initialGap - stageTop,
        endOffset: referenceBottomFromCard + endGap - stageTop,
      };
    }

    parseDistance(value, fallback) {
      const parsedValue = Number.parseFloat(value);
      if (!Number.isFinite(parsedValue)) return fallback;
      return this.clamp(parsedValue, 0, 160);
    }

    getMotionAmplitude(isDesktop) {
      const value = isDesktop
        ? this.dataset.motionAmplitudeDesktop
        : this.dataset.motionAmplitudeMobile;
      const parsedValue = Number.parseFloat(value);

      if (!Number.isFinite(parsedValue)) return 100;
      return this.clamp(parsedValue, 0, 300);
    }

    setRevealValue(value, clipValue = Math.max(value, 0)) {
      const normalizedValue = Math.abs(value) < 0.01 ? 0 : value;
      const normalizedClipValue = Math.abs(clipValue) < 0.01 ? 0 : clipValue;
      const formattedValue = `${normalizedValue.toFixed(2).replace(/\.00$/, '')}px`;
      const formattedClipValue = `${normalizedClipValue.toFixed(2).replace(/\.00$/, '')}px`;
      const motionState = `${formattedValue}|${formattedClipValue}`;
      if (motionState === this.lastRevealValue) return;

      this.style.setProperty('--vl-pdp-color-showcase-reveal-y', formattedValue);
      this.style.setProperty('--vl-pdp-color-showcase-reveal-clip', formattedClipValue);
      this.lastRevealValue = motionState;
    }

    clamp(value, minimum, maximum) {
      return Math.min(Math.max(value, minimum), maximum);
    }

    get isMotionEnabled() {
      const value = this.dataset.motionEnabled;
      return value !== 'false' && value !== '0' && value !== 'off';
    }

    get prefersReducedMotion() {
      return this.reducedMotionQuery?.matches ?? false;
    }

    get isColorAutoplayEnabled() {
      const value = this.dataset.colorAutoplay;
      return value === 'true' || value === '1' || value === 'on';
    }

    get colorAutoplayInterval() {
      const seconds = Number.parseFloat(this.dataset.colorAutoplayInterval);
      return (Number.isFinite(seconds) ? Math.max(seconds, 2) : 4) * 1000;
    }

    get canAutoplay() {
      return (
        this.isColorAutoplayEnabled &&
        this.autoplayInView &&
        !this.autoplayPausedByPointer &&
        !this.autoplayPausedByFocus &&
        !document.hidden &&
        !this.prefersReducedMotion &&
        !this.pendingIndex &&
        !this.transitionState
      );
    }
  }

  customElements.define('vl-pdp-color-showcase', VLPDPColorShowcase);
}
